import React, { useMemo } from 'react';
import {
  selection,
} from 'redux-resource-plugins';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';

import {
  getUiState,
} from 'web-selectors';

import {
  Button,
  Tooltip,
  Checkbox,
  NavigationTabs,
} from 'web-components';

import {
  Flex,
  Text,
  TextBreadcrumbs,
} from 'web-styled';

import {
  OpenedTooltip,
} from 'web-types';

const getSelectedCount = (count: number) => `${count} ${i18n.t('client', { count })}`;

const mapState = state => ({
  namespace: getUiState('namespace')(state),
});

interface Props {
  items: any[],
  isCustomer?: boolean,
  resourceType: string,
  openedTooltips: OpenedTooltip[],
  onRequestAdd?: { (): void },
  onRequestArchive: { (): void },
  onRequestRestore: { (): void },
  onRequestMoveItems: { (): void },
  onRequestDeleteItems: { (): void },
}

const Header = ({
  items,
  isCustomer,
  resourceType,
  openedTooltips,
  onRequestAdd,
  onRequestArchive,
  onRequestRestore,
  onRequestMoveItems,
  onRequestDeleteItems,
}: Props) => {
  const { namespace } = useSelector(mapState);
  const dispatch = useDispatch();

  const tabs = useMemo(() => {
    const base = ['individuals', 'groups'];
    if (namespace.symbol !== 'ideafit') {
      base.push('customers');
    }

    return [...base, 'archived'].map(
      name => ({
        name,
        to: `/clients/${name}`,
      }),
    );
  }, [namespace.symbol]);

  const onChecked = () => {
    if (items.every(item => item.selected)) {
      dispatch(
        selection.deselectResources(
          resourceType,
          items.map(item => item.id),
        ),
      );
    } else {
      dispatch(
        selection.selectResources(
          resourceType,
          items.map(item => item.id),
        ),
      );
    }
  };

  return (
    <Flex
      fillSpace
      align="center"
      justify="space-between"
    >
      {(() => {
        switch (resourceType) {
          case 'clients':
            return (
              <Flex
                align="center"
                spacing={10}
              >
                <Checkbox
                  onChange={onChecked}
                  checked={items.length > 0 && items.every(item => item.selected)}
                />
                <TextBreadcrumbs transform="capitalize">
                  {i18n.t('client_plural')}
                </TextBreadcrumbs>
                {!isCustomer && (
                  <Tooltip
                    body={i18n.t('tooltip.client.add.clients')}
                    toggleMode={openedTooltips.some(el => el.key === 'add_client')}
                    initialState={openedTooltips.some(el => el.key === 'add_client')}
                  >
                    <Button
                      icon="fa fa-plus"
                      appearance="success"
                      onClick={onRequestAdd}
                    >
                      {i18n.t('add.client')}
                    </Button>
                  </Tooltip>
                )}
                {items.some(item => item.selected) && (
                  <Flex
                    spacing={10}
                    align="center"
                  >
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onRequestMoveItems}
                    >
                      Move to group
                    </Button>
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onRequestArchive}
                    >
                      Archive
                    </Button>
                    <Button
                      ghost
                      appearance="danger"
                      onClick={onRequestDeleteItems}
                    >
                      Delete
                    </Button>
                    <Text
                      color="link"
                      weight={500}
                      size="12px"
                      transform="capitalize"
                    >
                      {getSelectedCount(items.filter(item => item.selected).length)}
                    </Text>
                  </Flex>
                )}
              </Flex>
            );
          case 'clientGroups':
            return (
              <Flex
                align="center"
                spacing={10}
              >
                <Checkbox
                  onChange={onChecked}
                  checked={items.length > 0 && items.every(item => item.selected)}
                />
                <TextBreadcrumbs>
                  Groups
                </TextBreadcrumbs>
                <Tooltip
                  body={i18n.t('tooltip.client.add.client_groups')}
                >
                  <Button
                    icon="fa fa-plus"
                    appearance="success"
                    onClick={onRequestAdd}
                  >
                    Add Group
                  </Button>
                </Tooltip>
                {items.some(item => item.selected) && (
                  <Flex
                    spacing={10}
                    align="center"
                  >
                    <Button
                      ghost
                      appearance="danger"
                      onClick={onRequestDeleteItems}
                    >
                      Delete
                    </Button>
                    <Text
                      color="link"
                      weight={500}
                      size="12px"
                    >
                      {items.filter(item => item.selected).length}
                      {' Group(s)'}
                    </Text>
                  </Flex>
                )}
              </Flex>
            );
          case 'archivedClients':
            return (
              <Flex
                align="center"
                spacing={10}
              >
                <Checkbox
                  onChange={onChecked}
                  checked={items.length > 0 && items.every(item => item.selected)}
                />
                <TextBreadcrumbs>
                  {i18n.t('client.archived.title')}
                </TextBreadcrumbs>
                {items.some(item => item.selected) && (
                  <Flex
                    spacing={10}
                    align="center"
                  >
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onRequestRestore}
                    >
                      Restore
                    </Button>
                    <Button
                      ghost
                      appearance="danger"
                      onClick={onRequestDeleteItems}
                    >
                      Delete
                    </Button>
                    <Text
                      color="link"
                      weight={500}
                      size="12px"
                      transform="capitalize"
                    >
                      {getSelectedCount(items.filter(item => item.selected).length)}
                    </Text>
                  </Flex>
                )}
              </Flex>
            );
          default:
            return null;
        }
      })()}
      <NavigationTabs
        tabs={tabs}
      />
    </Flex>
  );
};

Header.defaultProps = {
  isCustomer: false,
  onRequestAdd: undefined,
};

export default Header;
